import { useEffect, useState } from "react";
import {
  FiBell,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiSave,
  FiCalendar,
  FiClock,
  FiUsers,
  FiAlertCircle,
  FiCheckCircle,
} from "react-icons/fi";

import api from "./api/axios";
import "./adminAnnouncements.css";

function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    message: "",
    audience: "students",
    type: "manual",
    priority: "normal",
    deadline: "",
    status: "active",
  });

  const [alert, setAlert] = useState({
    type: "",
    text: "",
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        "/api/admin/announcements"
      );

      setAnnouncements(
        Array.isArray(res.data) ? res.data : []
      );
    } catch (err) {
      console.error(
        "Failed to load announcements:",
        err
      );

      showAlert(
        "error",
        "Failed to load announcements."
      );
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, text) => {
    setAlert({
      type,
      text,
    });

    setTimeout(() => {
      setAlert({
        type: "",
        text: "",
      });
    }, 3000);
  };

  const resetForm = () => {
    setForm({
      title: "",
      message: "",
      audience: "students",
      type: "manual",
      priority: "normal",
      deadline: "",
      status: "active",
    });

    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (announcement) => {
    setEditingId(announcement.id);

    setForm({
      title: announcement.title || "",
      message: announcement.message || "",
      audience: announcement.audience || "students",
      type: announcement.type || "manual",
      priority: announcement.priority || "normal",
      deadline: announcement.deadline
        ? formatDateTimeLocal(announcement.deadline)
        : "",
      status: announcement.status || "active",
    });

    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      showAlert(
        "error",
        "Please enter an announcement title."
      );
      return;
    }

    if (!form.message.trim()) {
      showAlert(
        "error",
        "Please enter an announcement message."
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        audience: form.audience,
        type: form.type,
        priority: form.priority,
        deadline: form.deadline || null,
        status: form.status,
      };

      if (editingId) {
        await api.put(
          `/api/admin/announcements/${editingId}`,
          payload
        );

        showAlert(
          "success",
          "Announcement updated successfully."
        );
      } else {
        await api.post(
          "/api/admin/announcements",
          payload
        );

        showAlert(
          "success",
          "Announcement created successfully."
        );
      }

      setShowModal(false);
      resetForm();

      await fetchAnnouncements();
    } catch (err) {
      console.error(
        "Failed to save announcement:",
        err
      );

      showAlert(
        "error",
        err.response?.data?.message ||
          "Failed to save announcement."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this announcement?"
    );

    if (!confirmed) return;

    try {
      await api.delete(
        `/api/admin/announcements/${id}`
      );

      setAnnouncements((prev) =>
        prev.filter(
          (announcement) =>
            announcement.id !== id
        )
      );

      showAlert(
        "success",
        "Announcement deleted successfully."
      );
    } catch (err) {
      console.error(
        "Failed to delete announcement:",
        err
      );

      showAlert(
        "error",
        err.response?.data?.message ||
          "Failed to delete announcement."
      );
    }
  };

  const formatDateTimeLocal = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    const hours = String(
      date.getHours()
    ).padStart(2, "0");

    const minutes = String(
      date.getMinutes()
    ).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formatDeadline = (deadline) => {
    if (!deadline) {
      return "No deadline";
    }

    const date = new Date(deadline);

    if (Number.isNaN(date.getTime())) {
      return "Invalid deadline";
    }

    return date.toLocaleString("en-PH", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case "all":
        return "Everyone";

      case "students":
        return "Students";

      case "faculty":
        return "Faculty";

      default:
        return audience || "Students";
    }
  };

  return (
    <div className="admin-announcements-page">

      <div className="admin-announcements-header">
        <div>
          <div className="admin-announcements-kicker">
            YEARBOOK MANAGEMENT
          </div>

          <h1>Announcements</h1>

          <p>
            Create announcements, reminders, and
            deadlines for yearbook participants.
          </p>
        </div>

        <button
          className="announcement-add-btn"
          onClick={openAddModal}
        >
          <FiPlus />
          <span>New Announcement</span>
        </button>
      </div>

      {alert.text && (
        <div
          className={`announcement-alert ${
            alert.type === "success"
              ? "alert-success"
              : "alert-error"
          }`}
        >
          {alert.type === "success" ? (
            <FiCheckCircle />
          ) : (
            <FiAlertCircle />
          )}

          <span>{alert.text}</span>
        </div>
      )}

      <div className="announcement-summary">

        <div className="summary-card">
          <div className="summary-icon">
            <FiBell />
          </div>

          <div>
            <span>Total Announcements</span>
            <strong>
              {announcements.length}
            </strong>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon active">
            <FiCheckCircle />
          </div>

          <div>
            <span>Active</span>
            <strong>
              {
                announcements.filter(
                  (item) =>
                    item.status === "active"
                ).length
              }
            </strong>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon deadline">
            <FiCalendar />
          </div>

          <div>
            <span>With Deadline</span>
            <strong>
              {
                announcements.filter(
                  (item) => item.deadline
                ).length
              }
            </strong>
          </div>
        </div>

      </div>

      <div className="announcement-container">

        <div className="announcement-container-header">
          <div>
            <h2>Announcement List</h2>

            <p>
              Manage the messages shown to
              students and faculty.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="announcement-loading">
            <div className="announcement-spinner"></div>

            <span>
              Loading announcements...
            </span>
          </div>
        ) : announcements.length === 0 ? (
          <div className="announcement-empty">

            <div className="empty-icon">
              <FiBell />
            </div>

            <h3>
              No announcements yet
            </h3>

            <p>
              Create your first announcement
              or yearbook deadline.
            </p>

            <button
              className="empty-add-btn"
              onClick={openAddModal}
            >
              <FiPlus />
              Create Announcement
            </button>

          </div>
        ) : (
          <div className="announcement-table-wrapper">

            <table className="announcement-table">

              <thead>
                <tr>
                  <th>Announcement</th>
                  <th>Audience</th>
                  <th>Priority</th>
                  <th>Deadline</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>

                {announcements.map(
                  (announcement) => (
                    <tr
                      key={announcement.id}
                    >

                      <td>
                        <div className="announcement-info">

                          <div className="announcement-list-icon">
                            <FiBell />
                          </div>

                          <div>
                            <strong>
                              {
                                announcement.title
                              }
                            </strong>

                            <p>
                              {
                                announcement.message
                              }
                            </p>

                            <small>
                              {announcement.type ===
                              "automatic"
                                ? "Automatic"
                                : "Manual"}
                            </small>
                          </div>

                        </div>
                      </td>

                      <td>
                        <span className="audience-badge">
                          <FiUsers />

                          {getAudienceLabel(
                            announcement.audience
                          )}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`priority-badge ${
                            announcement.priority ===
                            "important"
                              ? "priority-important"
                              : "priority-normal"
                          }`}
                        >
                          {announcement.priority ===
                          "important"
                            ? "Important"
                            : "Normal"}
                        </span>
                      </td>

                      <td>
                        {announcement.deadline ? (
                          <div className="deadline-info">

                            <FiCalendar />

                            <div>
                              <strong>
                                {formatDeadline(
                                  announcement.deadline
                                )}
                              </strong>

                              <small>
                                Submission deadline
                              </small>
                            </div>

                          </div>
                        ) : (
                          <span className="no-deadline">
                            No deadline
                          </span>
                        )}
                      </td>

                      <td>
                        <span
                          className={`status-badge ${
                            announcement.status ===
                            "active"
                              ? "status-active"
                              : "status-inactive"
                          }`}
                        >
                          <span className="status-dot"></span>

                          {announcement.status ===
                          "active"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td>
                        <div className="announcement-actions">

                          <button
                            className="action-btn edit"
                            onClick={() =>
                              openEditModal(
                                announcement
                              )
                            }
                            title="Edit announcement"
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            className="action-btn delete"
                            onClick={() =>
                              handleDelete(
                                announcement.id
                              )
                            }
                            title="Delete announcement"
                          >
                            <FiTrash2 />
                          </button>

                        </div>
                      </td>

                    </tr>
                  )
                )}

              </tbody>
            </table>

          </div>
        )}

      </div>

      {showModal && (
        <div
          className="announcement-modal-overlay"
          onMouseDown={(e) => {
            if (
              e.target ===
              e.currentTarget
            ) {
              closeModal();
            }
          }}
        >

          <div className="announcement-modal">

            <div className="announcement-modal-header">

              <div>
                <div className="modal-icon">
                  <FiBell />
                </div>

                <div>
                  <h2>
                    {editingId
                      ? "Edit Announcement"
                      : "New Announcement"}
                  </h2>

                  <p>
                    {editingId
                      ? "Update this announcement."
                      : "Create a new announcement for your yearbook."}
                  </p>
                </div>
              </div>

              <button
                className="modal-close-btn"
                onClick={closeModal}
                disabled={saving}
              >
                <FiX />
              </button>

            </div>

            <form
              className="announcement-form"
              onSubmit={handleSubmit}
            >

              <div className="form-group full">
                <label>
                  Announcement Title
                  <span>*</span>
                </label>

                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g. Yearbook Profile Submission Deadline"
                  maxLength={255}
                  required
                />
              </div>

              <div className="form-group full">
                <label>
                  Message
                  <span>*</span>
                </label>

                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Write the announcement message..."
                  rows="5"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Audience
                </label>

                <select
                  name="audience"
                  value={form.audience}
                  onChange={handleChange}
                >
                  <option value="students">
                    Students
                  </option>

                  <option value="faculty">
                    Faculty
                  </option>

                  <option value="all">
                    Everyone
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Priority
                </label>

                <select
                  name="priority"
                  value={form.priority}
                  onChange={handleChange}
                >
                  <option value="normal">
                    Normal
                  </option>

                  <option value="important">
                    Important
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Deadline
                </label>

                <div className="input-with-icon">
                  <FiCalendar />

                  <input
                    type="datetime-local"
                    name="deadline"
                    value={form.deadline}
                    onChange={handleChange}
                  />
                </div>

                <small className="form-help">
                  Leave empty if there is no
                  deadline.
                </small>
              </div>

              <div className="form-group">
                <label>
                  Status
                </label>

                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="active">
                    Active
                  </option>

                  <option value="inactive">
                    Inactive
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Announcement Type
                </label>

                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                >
                  <option value="manual">
                    Manual
                  </option>

                  <option value="automatic">
                    Automatic
                  </option>
                </select>
              </div>

              {form.deadline && (
                <div className="deadline-preview full">

                  <FiClock />

                  <div>
                    <strong>
                      Deadline Set
                    </strong>

                    <span>
                      {formatDeadline(
                        form.deadline
                      )}
                    </span>
                  </div>

                </div>
              )}

              <div className="announcement-form-actions full">

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="save-btn"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="button-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave />

                      {editingId
                        ? "Save Changes"
                        : "Create Announcement"}
                    </>
                  )}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </div>
  );
}

export default AdminAnnouncements;